import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { LogOut, Car, DollarSign, MapPin, Calendar, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function DriverDashboardPage() {
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [rides, setRides] = useState({ pending_bookings: [], accepted_bookings: [], past_bookings: [] });
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rideFromDate, setRideFromDate] = useState("");
  const [rideToDate, setRideToDate] = useState("");
  const [rideStatusFilter, setRideStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    const driverData = localStorage.getItem('driver_data');
    const token = localStorage.getItem('driver_token');

    if (!driverData || !token) {
      toast.error('Please login first');
      navigate('/driver/login');
      return;
    }

    const parsedDriver = JSON.parse(driverData);
    setDriver(parsedDriver);
    fetchRides(parsedDriver.id);
    fetchEarnings(parsedDriver.id);
    
    // Start location tracking
    startLocationTracking(parsedDriver.id);
  }, [navigate]);

  const startLocationTracking = (driverId) => {
    // Update location every 10 seconds if geolocation is available
    if ('geolocation' in navigator) {
      const updateLocation = () => {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              await axios.put(`${API}/api/driver/location/${driverId}`, {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              });
              console.log('Location updated');
            } catch (error) {
              console.error('Failed to update location:', error);
            }
          },
          (error) => {
            console.error('Geolocation error:', error);
          }
        );
      };

      // Update immediately
      updateLocation();

      // Update every 10 seconds
      const intervalId = setInterval(updateLocation, 10000);

      // Cleanup on unmount
      return () => clearInterval(intervalId);
    } else {
      console.warn('Geolocation not supported');
    }
  };

  const fetchRides = async (driverId) => {
    try {
      const response = await axios.get(`${API}/api/driver/rides/${driverId}`);
      setRides(response.data);
    } catch (error) {
      toast.error('Failed to fetch rides');
    }
  };

  const handleStartRide = async (bookingId) => {
    try {
      await axios.post(`${API}/api/driver/rides/${bookingId}/start?driver_id=${driver.id}`);
      toast.success('Ride started! Customer has been notified.');
      fetchRides(driver.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start ride');
    }
  };

  const handleCompleteRide = async (bookingId) => {
    if (!window.confirm('Are you sure you want to mark this ride as completed?')) {
      return;
    }
    
    try {
      await axios.post(`${API}/api/driver/rides/${bookingId}/complete?driver_id=${driver.id}`);
      toast.success('Ride completed! Customer and admin have been notified.');
      fetchRides(driver.id);
      fetchEarnings(driver.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete ride');
    }
  };

  const fetchEarnings = async (driverId) => {
    try {
      const response = await axios.get(`${API}/api/driver/earnings/${driverId}`);
      setEarnings(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch earnings');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_data');
    toast.success('Logged out successfully');
    navigate('/driver/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Driver Dashboard</h1>
            <p className="text-purple-100 mt-1">Welcome back, {driver?.name}!</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="bg-white text-purple-600 hover:bg-gray-100">
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <DollarSign className="mx-auto mb-2 text-green-600" size={32} />
                <p className="text-sm text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold">£{earnings?.total_earnings || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <DollarSign className="mx-auto mb-2 text-blue-600" size={32} />
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-bold">£{earnings?.monthly_earnings || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Car className="mx-auto mb-2 text-purple-600" size={32} />
                <p className="text-sm text-gray-600">Total Rides</p>
                <p className="text-2xl font-bold">{earnings?.total_rides || 0}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <Badge className="mb-2" variant={driver?.driver_type === 'dsa' ? 'default' : 'secondary'}>
                  {driver?.driver_type?.toUpperCase()}
                </Badge>
                <p className="text-sm text-gray-600">Commission Rate</p>
                <p className="text-2xl font-bold">{earnings?.commission_rate * 100}%</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="rides" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="rides">My Rides</TabsTrigger>
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
          </TabsList>

          {/* Rides Tab */}
          <TabsContent value="rides">
            {/* Pending Rides - Needs Action */}
            {rides.pending_bookings && rides.pending_bookings.length > 0 && (
              <Card className="mb-6 border-2 border-yellow-400">
                <CardHeader className="bg-yellow-50">
                  <CardTitle className="flex items-center gap-2 text-yellow-800">
                    <Clock size={20} />
                    Pending Rides - Action Required ({rides.pending_bookings.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {rides.pending_bookings.map((ride) => (
                      <div key={ride.id} className="border-2 border-yellow-300 rounded-lg p-4 bg-yellow-50">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <Badge className="bg-yellow-500">Awaiting Response</Badge>
                            <p className="text-sm text-gray-500 mt-1">Booking #{ride.id.substring(0, 8)}</p>
                          </div>
                          <p className="font-bold text-lg text-green-600">£{ride.driver_earnings}</p>
                        </div>
                        
                        <div className="space-y-2 text-sm mb-4">
                          <div className="flex items-start gap-2">
                            <MapPin size={16} className="mt-1 text-green-600" />
                            <div>
                              <p className="font-semibold">Pickup:</p>
                              <p className="text-gray-600">{ride.pickup_location?.address}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <MapPin size={16} className="mt-1 text-red-600" />
                            <div>
                              <p className="font-semibold">Drop-off:</p>
                              <p className="text-gray-600">{ride.dropoff_location?.address}</p>
                            </div>
                          </div>
                          <div className="flex gap-4 mt-2">
                            <div className="flex items-center gap-1">
                              <Calendar size={16} />
                              <span>{ride.booking_date}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock size={16} />
                              <span>{ride.booking_time}</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-gray-600"><strong>Customer:</strong> {ride.customer_name}</p>
                            <p className="text-gray-600"><strong>Phone:</strong> {ride.customer_phone}</p>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button 
                            onClick={() => handleAcceptRide(ride.id)}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            ✅ Accept Ride
                          </Button>
                          <Button 
                            onClick={() => handleRejectRide(ride.id)}
                            variant="outline"
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            ❌ Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Accepted/Upcoming Rides */}
            <Card>
              <CardHeader>
                <CardTitle>Accepted Rides</CardTitle>
              <div style={{display:"flex",gap:"10px",margin:"10px 0",flexWrap:"wrap"}}>
                <div><label style={{fontSize:"12px",display:"block"}}>From</label><input type="date" value={rideFromDate} onChange={e=>setRideFromDate(e.target.value)} style={{padding:"6px",border:"1px solid #ccc",borderRadius:"4px"}} data-testid="ride-from-date" /></div>
                <div><label style={{fontSize:"12px",display:"block"}}>To</label><input type="date" value={rideToDate} onChange={e=>setRideToDate(e.target.value)} style={{padding:"6px",border:"1px solid #ccc",borderRadius:"4px"}} data-testid="ride-to-date" /></div><div><label style={{fontSize:"12px",display:"block"}}>Status</label><select value={rideStatusFilter} onChange={e=>setRideStatusFilter(e.target.value)} style={{padding:"6px",border:"1px solid #ccc",borderRadius:"4px"}} data-testid="ride-status-filter"><option value="all">All</option><option value="pending">Pending</option><option value="completed">Completed</option></select></div>
                {(rideFromDate || rideToDate) && (<button onClick={()=>{setRideFromDate("");setRideToDate("");}} style={{padding:"6px 12px",background:"#eee",border:"1px solid #ccc",borderRadius:"4px",alignSelf:"flex-end"}} data-testid="ride-clear-filter">Clear</button>)}<button onClick={()=>setSortOrder(sortOrder==="asc"?"desc":"asc")} style={{padding:"6px 12px",background:"#fff",border:"1px solid #ccc",borderRadius:"4px",cursor:"pointer",alignSelf:"flex-end"}} data-testid="sort-toggle-btn" title="Toggle sort">{sortOrder==="asc"?"Date ↑":"Date ↓"}</button>
              </div>
              </CardHeader>
              <CardContent>
                {rides.accepted_bookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No upcoming rides
                  </div>
                ) : (
                  <div className="space-y-4">
                    {[...rides.accepted_bookings].filter(r => { const d = r.pickup_date || r.booking_date || ''; if (rideFromDate && d < rideFromDate) return false; if (rideToDate && d > rideToDate) return false; if (rideStatusFilter === 'completed' && r.status !== 'completed') return false; if (rideStatusFilter === 'pending' && ['completed','cancelled'].includes(r.status)) return false; return true; }).sort((a,b) => { const da = (a.pickup_date || a.booking_date || '') + ' ' + (a.pickup_time || a.booking_time || ''); const db = (b.pickup_date || b.booking_date || '') + ' ' + (b.pickup_time || b.booking_time || ''); return sortOrder === 'asc' ? da.localeCompare(db) : db.localeCompare(da); }).map((ride) => (
                      <div key={ride.id} className="border-2 rounded-lg p-4 hover:bg-gray-50" style={{
                        borderColor: ride.status === 'on_the_way' ? '#f59e0b' : 
                                     ride.status === 'arrived' ? '#10b981' : '#8b5cf6'
                      }}>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <Badge className={
                              ride.status === 'on_the_way' ? 'bg-yellow-500' : 
                              ride.status === 'arrived' ? 'bg-green-500' : 'bg-purple-500'
                            }>
                              {ride.status === 'on_the_way' ? '🚗 On The Way' : 
                               ride.status === 'arrived' ? '📍 Arrived' : 
                               ride.status === 'confirmed' ? '✅ Confirmed' : ride.status}
                            </Badge>
                            <p className="text-sm text-gray-500 mt-1">Booking #{ride.id.substring(0, 8)}</p>
                          </div>
                          <p className="font-bold text-lg">£{ride.price}</p>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <MapPin size={16} className="mt-1 text-green-600" />
                            <div>
                              <p className="font-semibold">Pickup:</p>
                              <p className="text-gray-600">{ride.pickup_location?.address}</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <MapPin size={16} className="mt-1 text-red-600" />
                            <div>
                              <p className="font-semibold">Drop-off:</p>
                              <p className="text-gray-600">{ride.dropoff_location?.address}</p>
                            </div>
                          </div>
                          <div className="flex gap-4 mt-2">
                            <div className="flex items-center gap-1">
                              <Calendar size={16} />
                              <span>{ride.booking_date}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock size={16} />
                              <span>{ride.booking_time}</span>
                            </div>
                          </div>
                          <div className="bg-gray-100 p-2 rounded mt-2">
                            <p className="text-gray-700"><strong>Customer:</strong> {ride.customer_name}</p>
                            <p className="text-gray-700"><strong>Phone:</strong> <a href={"tel:" + ride.customer_phone} className="text-blue-600">{ride.customer_phone}</a></p>
                          </div>
                          <p className="text-purple-600 font-semibold">
                            Your Earnings: £{ride.driver_earnings}
                          </p>
                        </div>
                        
                        {/* Action Buttons based on ride status */}
                        <div className="flex gap-2 mt-4">
                          {(ride.status === 'confirmed' || ride.status === 'assigned') && (
                            <Button 
                              onClick={() => handleStartRide(ride.id)}
                              className="flex-1 bg-blue-600 hover:bg-blue-700"
                              data-testid="start-ride-btn"
                            >
                              🚗 Start Ride
                            </Button>
                          )}
                          {(ride.status === 'on_the_way' || ride.status === 'in_progress' || ride.status === 'arrived') && (
                            <Button 
                              onClick={() => handleCompleteRide(ride.id)}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                              data-testid="complete-ride-btn"
                            >
                              ✅ Complete Ride
                            </Button>
                          )}
                          <Button 
                            variant="outline"
                            onClick={() => window.open("tel:" + ride.customer_phone, "_self")}
                            className="px-4"
                          >
                            📞 Call
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Past Rides</CardTitle>
              </CardHeader>
              <CardContent>
                {rides.past_bookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No past rides
                  </div>
                ) : (
                  <div className="space-y-4">
                    {rides.past_bookings.slice(0, 10).map((ride) => (
                      <div key={ride.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <Badge variant="outline">{ride.status}</Badge>
                            <p className="text-sm text-gray-500 mt-1">Booking #{ride.id.substring(0, 8)}</p>
                          </div>
                          <p className="font-bold text-lg text-green-600">£{ride.driver_earnings}</p>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p>{ride.pickup_location?.address} → {ride.dropoff_location?.address}</p>
                          <p className="mt-1">{ride.booking_date} at {ride.booking_time}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Earnings Tab */}
          <TabsContent value="earnings">
            <Card>
              <CardHeader>
                <CardTitle>Earnings Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {earnings?.earnings_per_ride?.slice(0, 20).map((earning, index) => (
                    <div key={index} className="flex justify-between items-center border-b pb-3">
                      <div className="flex-1">
                        <p className="font-semibold">#{earning.booking_id.substring(0, 8)}</p>
                        <p className="text-sm text-gray-600">{earning.booking_date}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {earning.pickup} → {earning.dropoff}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">£{earning.driver_earnings}</p>
                        <p className="text-xs text-gray-500">of £{earning.total_fare}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Driver Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-semibold">{driver?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-semibold">{driver?.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-semibold">{driver?.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">License Plate</p>
                      <p className="font-semibold">{driver?.license_plate}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Vehicle Type</p>
                      <p className="font-semibold">{driver?.vehicle_type?.toUpperCase()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Driver Type</p>
                      <Badge>{driver?.driver_type?.toUpperCase()}</Badge>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="font-semibold">{driver?.address}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default DriverDashboardPage;
